import { USER_ACCESS, USER_ACCESS_TO_LABEL } from "@/constants";
import { ROUTES } from "@/routes_new";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@components/ui/table";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { Trash2Icon } from "lucide-react";
import { Link, generatePath } from "react-router-dom";
import { Button } from "@components/ui/button";
import { Organization } from "@/types/Organization";
import { User } from "@/types/User";
import { Badge } from "@components/ui/badge";

type UsersTableProps = {
  canManageUsers: boolean;
  orgUuid: Organization["uuid"];
  users: User[];
};

export default function UsersTable({
  canManageUsers,
  users,
  orgUuid,
}: UsersTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <Trans>Namn</Trans>
          </TableHead>
          <TableHead>
            <Trans>E-post</Trans>
          </TableHead>
          <TableHead>
            <Trans>Roll</Trans>
          </TableHead>
          <TableHead>
            <Trans>Status</Trans>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users
          .toSorted((a, b) => {
            // Active users before invited
            if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;

            // Admins first
            const aIsAdmin = a.user_type === USER_ACCESS.ADMIN;
            const bIsAdmin = b.user_type === USER_ACCESS.ADMIN;
            if (aIsAdmin !== bIsAdmin) return aIsAdmin ? -1 : 1;

            // Alphabetically by name or email
            const aName = a.name || a.email;
            const bName = b.name || b.email;
            return aName.localeCompare(bName);
          })
          .map((user) => (
            <TableRow key={user.id} data-testid="user-settings-user-row">
              <TableCell>
                <Link
                  to={generatePath(ROUTES.SETTINGS_TEAM_USER_ORGID, {
                    orgUuid,
                    userId: String(user.id),
                  })}
                  className="link"
                  data-testid="user-settings-open-modal"
                >
                  {user.name}
                </Link>
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <Badge
                  variant={
                    user.user_type === USER_ACCESS.ADMIN ? "cyan" : "secondary"
                  }
                >
                  {i18n._(USER_ACCESS_TO_LABEL[user.user_type])}
                </Badge>
              </TableCell>
              <TableCell data-testid="user-settings-status">
                {user.is_active ? (
                  <span className="text-primary">
                    <Trans>Aktiv</Trans>
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    <Trans>Inbjuden</Trans>
                  </span>
                )}
              </TableCell>
              {canManageUsers && (
                <TableCell className="flex items-center gap-2 text-right justify-end">
                  <Button size="sm" type="button" variant="outline" asChild>
                    <Link
                      to={generatePath(ROUTES.SETTINGS_TEAM_USER_ORGID, {
                        orgUuid,
                        userId: String(user.id),
                      })}
                    >
                      <Trans>Redigera</Trans>
                    </Link>
                  </Button>

                  <Button
                    asChild
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    data-testid="user-settings-delete-user"
                  >
                    <Link
                      to={generatePath(ROUTES.SETTINGS_TEAM_DELETE_USER_ORGID, {
                        orgUuid,
                        userId: String(user.id),
                      })}
                    >
                      <Trash2Icon className="text-destructive-foreground" />
                      <span className="sr-only">
                        <Trans>Ta bort användare</Trans>
                      </span>
                    </Link>
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
}
